//client\src\components\SearchUsers.js

import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import { authFetch } from '../Utilities/Api';
import './SearchUsers.css';
import ProfileImage from './ProfileImage';

const SearchUsers = () => {
    const { user } = useContext(AuthContext);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [error, setError] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    // 🟢 ENHANCEMENT 1: Extract fetch logic into a useCallback for stability and clean separation
    const fetchUsers = useCallback(async (searchQuery) => {
        if (!searchQuery.trim()) {
            setResults([]);
            setShowDropdown(false);
            setActiveIndex(-1);
            return;
        }

        try {
            const res = await authFetch(`/users/search?q=${encodeURIComponent(searchQuery)}`);
            if (!res.ok) throw new Error('Failed to fetch users');
            const data = await res.json();
            setResults(data);
            setShowDropdown(true);
            // 🟢 FIX: Set activeIndex only if results are different and non-empty
            setActiveIndex(data.length ? 0 : -1);
        } catch (err) {
            console.error('❌ Search error:', err);
            setError('Something went wrong while searching.');
            setShowDropdown(true); // Keep dropdown open to show error
        }
    }, [setResults, setShowDropdown, setActiveIndex, setError]);

    // Fetch users (debounced)
    useEffect(() => {
        // Clear previous error when query changes
        setError('');

        // Debounce the fetch call
        const delay = setTimeout(() => fetchUsers(query), 300);

        return () => clearTimeout(delay);
    }, [query, fetchUsers]); // fetchUsers is a stable dependency

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!user) return null;

    const listboxId = 'search-users-listbox';

    // Keyboard handling on the input (no changes needed, it's perfect)
    const onKeyDown = (e) => {
        if (!showDropdown || !results.length) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % results.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + results.length) % results.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < results.length) {
                const u = results[activeIndex];
                setShowDropdown(false);
                navigate(`/user/${u.username}`);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setShowDropdown(false);
        }
    };

    return (
        <div className="search-container" ref={dropdownRef}>
            <input
                type="text"
                className="search-input"
                placeholder="Search users..."
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setError('');
                }}
                // 🟢 FIX: Only show dropdown if there's a query OR an error to display
                onFocus={() => (query || error) && setShowDropdown(true)}
                onKeyDown={onKeyDown}
                // ARIA combobox pattern (list autocomplete)
                role="combobox"
                aria-expanded={showDropdown}
                aria-autocomplete="list"
                aria-controls={listboxId}
                aria-activedescendant={
                    activeIndex >= 0 && results[activeIndex]
                        ? `option-${results[activeIndex]._id}`
                        : undefined
                }
                aria-label="Search for users"
            />

            {error && <p className="search-error">{error}</p>}

            {showDropdown && query && (
                <>
                    {results.length > 0 ? (
                        <ul
                            className="search-results"
                            role="listbox"
                            aria-label="User results"
                            id={listboxId}
                        >
                            {results.map((u, index) => (
                                <li
                                    key={u._id}
                                    id={`option-${u._id}`}
                                    role="option"
                                    aria-selected={index === activeIndex}
                                    className={index === activeIndex ? 'is-active' : undefined}
                                    onMouseEnter={() => setActiveIndex(index)}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        setShowDropdown(false);
                                        navigate(`/user/${u.username}`);
                                    }}
                                >
                                    <Link
                                        to={`/user/${u.username}`}
                                        className="search-user-link"
                                        tabIndex={-1}
                                    >
                                        <ProfileImage
                                            src={u.profilePicture}
                                            className="avatar"
                                            alt={`${u.nickname || u.username}'s avatar`}
                                        />
                                        <div>
                                            <strong>{u.nickname || u.username}</strong>
                                            <p>@{u.username}</p>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        !error && <p className="search-empty">No users found.</p>
                    )}
                </>
            )}
        </div>
    );
};

export default SearchUsers;